import { useMemo } from 'react';
import {
    Box,
    Combobox,
    ComboboxInput,
    ComboboxList,
    ComboboxOption,
    ComboboxPopover,
    ComboboxSection,
    FlexBox,
    Typography,
    tokens,
} from '@vp/swan';
import styled from 'styled-components';
import { __ } from '@99designs/i18n';
import {
    FormattedTimezone,
    REGION_ORDER,
    TIMEZONE_DATA,
    getFormattedTimezonesByRegion,
    getOrCreateFormattedTimezone,
} from '../../lib/timezones';

const StyledComboboxList = styled(ComboboxList)`
    .swan-listbox-section-title {
        font-size: ${tokens.SwanSemFontSizeSmall};
        font-weight: ${tokens.SwanBaseFontWeightBold};
        color: ${tokens.SwanSemColorTextSubtle};
    }
`;

export interface TimezoneSelectorProps {
    /** Currently selected IANA timezone identifier */
    value: string;
    /** Callback when timezone selection changes */
    onChange: (timezone: string) => void;
    /** Whether the selector is disabled */
    disabled?: boolean;
    /** Error message to display */
    error?: string;
    /** Test ID for the component */
    'data-testid'?: string;
    /** Browser's auto-detected timezone IANA identifier (shows "Detected" label) */
    browserTimezone?: string;
    /** BCP 47 locale string for country name localization (e.g., "fr-FR", "de-DE") */
    locale?: string;
}

function TimezoneOptionContent({
    tz,
    isDetected,
}: {
    tz: FormattedTimezone;
    isDetected?: boolean;
}) {
    return (
        <FlexBox
            flexDirection={{ xs: 'column', lg: 'row' }}
            alignItems={{ xs: 'flex-start', lg: 'center' }}
            justifyContent={{ xs: 'flex-start', lg: 'space-between' }}
            style={{ width: '100%', gap: '4px 16px' }}
        >
            <Typography as="span" style={{ flexShrink: 0 }}>
                {tz.displayName}
                {isDetected && (
                    <Typography as="span" fontSkin="body-small" textColor="accent">
                        {' · '}
                        {__('Detected')}
                    </Typography>
                )}
            </Typography>
            <Typography
                as="span"
                fontSkin="body-small"
                textColor="subtle"
                style={{ whiteSpace: 'nowrap' }}
            >
                {tz.offset} • {tz.currentTime} {tz.icon}
            </Typography>
        </FlexBox>
    );
}

/**
 * A searchable timezone selector with grouped timezones by region.
 * Displays timezones in "City, Country (Abbreviation)" format.
 */
export function TimezoneSelector({
    value,
    onChange,
    disabled = false,
    error,
    browserTimezone,
    locale = 'en-US',
    'data-testid': testId = 'timezone-selector',
}: TimezoneSelectorProps) {
    const referenceDate = useMemo(() => new Date(), []);

    const timezonesByRegion = useMemo(
        () => getFormattedTimezonesByRegion(referenceDate, locale),
        [referenceDate, locale]
    );

    // If the browser timezone isn't in the curated list, create a formatted entry
    // so it always appears as a selectable option in the dropdown.
    const detectedTimezoneEntry = useMemo(() => {
        if (!browserTimezone) return null;
        const isInList = TIMEZONE_DATA.some((tz) => tz.iana === browserTimezone);
        if (isInList) return null;
        return getOrCreateFormattedTimezone(browserTimezone, referenceDate, locale);
    }, [browserTimezone, referenceDate, locale]);

    const handleSelectionChange = (selection: React.Key | null) => {
        if (selection && typeof selection === 'string') {
            onChange(selection);
        }
    };

    const comboBoxItems = useMemo(() => {
        const items: React.ReactElement[] = [];

        // If the browser timezone isn't in the curated list, add it at the top
        if (detectedTimezoneEntry) {
            items.push(
                <ComboboxOption
                    key={detectedTimezoneEntry.iana}
                    textValue={detectedTimezoneEntry.displayName}
                >
                    <TimezoneOptionContent tz={detectedTimezoneEntry} isDetected />
                </ComboboxOption>
            );
        }

        for (const region of REGION_ORDER) {
            const timezones = timezonesByRegion.get(region);
            if (!timezones || timezones.length === 0) continue;

            items.push(
                <ComboboxSection title={region} key={region}>
                    {timezones.map((tz: FormattedTimezone) => (
                        <ComboboxOption key={tz.iana} textValue={tz.displayName}>
                            <TimezoneOptionContent
                                tz={tz}
                                isDetected={tz.iana === browserTimezone}
                            />
                        </ComboboxOption>
                    ))}
                </ComboboxSection>
            );
        }

        return items;
    }, [timezonesByRegion, detectedTimezoneEntry, browserTimezone]);

    const errorId = `${testId}-error`;

    return (
        <Box>
            <Combobox
                selectedKey={value || undefined}
                onSelectionChange={handleSelectionChange}
                menuTrigger="focus"
            >
                <ComboboxInput
                    placeholder={__('Start typing a city...')}
                    disabled={disabled}
                    data-testid={testId}
                    aria-label={__('Select a timezone')}
                    aria-invalid={!!error}
                    aria-describedby={error ? errorId : undefined}
                />
                <ComboboxPopover>
                    <StyledComboboxList data-testid={`${testId}-list`}>
                        {comboBoxItems}
                    </StyledComboboxList>
                </ComboboxPopover>
            </Combobox>
            {error && (
                <Typography
                    role="alert"
                    id={errorId}
                    marginTop="3"
                    textColor="error"
                    fontSkin="body-small"
                >
                    {error}
                </Typography>
            )}
        </Box>
    );
}

export default TimezoneSelector;
